import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Article — "Root-cause analysis without a data scientist".
 *
 * Author's intended goal: "Analytics" (correlation + commonality in plain terms).
 * The published `ArticleGoal` union is currently Educational | ROI | Technical,
 * so this is filed under "Technical" — it explains how the analysis works under
 * the hood. If an "Analytics" goal is added to the union later, switch `goal`.
 *
 * Honest, vendor-neutral body; the product appears only in the closing block.
 * Every figure is illustrative and reads as a generic range.
 */
export const ROOT_CAUSE_WITHOUT_DATA_SCIENTIST_ARTICLE: Article = {
  slug: "root-cause-analysis-without-a-data-scientist",
  title: "Root-cause analysis without a data scientist",
  deck:
    "Finding why a defect spikes is usually framed as a job for a specialist with a notebook full of statistics. It is not. Two plain ideas — does the defect move with a parameter, and what do the bad parts have in common — get you most of the way, no PhD required.",
  goal: "Technical",
  readingMins: 8,
  heroStat: { value: "2 questions", label: "correlation and commonality — most root cause lives here" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "When porosity suddenly climbs on a Tuesday, the room fills with theories. Someone blames the sand, someone blames the new returns supplier, someone blames the night shift. Everyone is reasoning from memory, and memory is a poor instrument. The promise of analytics is supposed to fix this, but it usually arrives wrapped in language — regression, p-values, feature importance — that makes plant people assume they need to hire a data scientist before they can ask a simple question. They do not. Almost all of practical root-cause analysis reduces to two questions an operator already understands, made rigorous by writing them down.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Question one",
      heading: "Does the defect move when a parameter moves?",
      sub: "This is correlation — and it is just a measure of 'these two tend to rise and fall together'.",
    },
    {
      kind: "paragraph",
      text:
        "Correlation has a scary name and a simple meaning: when one number goes up, does the other tend to go up too? Plot pour temperature against misrun rate across a few hundred heats and the answer is usually visible to the naked eye before any statistic is computed. If low pour temperatures cluster with high misrun, you have a lead. A correlation coefficient just puts a number on how tight that relationship is — close to one or minus one means strong, close to zero means there is probably nothing there. You read it the way you read a fuel gauge, not the way you read a theorem.",
    },
    {
      kind: "chart",
      chart: "line",
      title: "Illustrative: misrun rate vs. pour temperature",
      xKey: "pourTemp",
      unit: "%",
      series: [{ dataKey: "misrun", label: "Misrun rate", tone: "danger" }],
      data: [
        { pourTemp: "1360", misrun: 7.8 },
        { pourTemp: "1375", misrun: 6.1 },
        { pourTemp: "1390", misrun: 4.3 },
        { pourTemp: "1405", misrun: 2.6 },
        { pourTemp: "1420", misrun: 1.5 },
        { pourTemp: "1435", misrun: 1.1 },
      ],
      caption:
        "Generic, illustrative figures. The downward slope is the signal: as pour temperature falls, misruns rise. A real plant would see scatter — but the trend is what you act on.",
    },
    {
      kind: "paragraph",
      text:
        "The honest caveat fits in one sentence: correlation is not proof of cause. Ice-cream sales and drowning both rise in summer without one causing the other. But on a foundry floor the candidate variables are not random — they are the physical inputs you already suspect, and a strong correlation between pour temperature and misrun is not a coincidence dressed up as one. Correlation does not close the case; it tells you where to point the rest of your judgement.",
    },
    {
      kind: "callout",
      tone: "info",
      title: "Read it with your process knowledge, not instead of it",
      text:
        "A correlation is a lead, not a verdict. The metallurgist's job is to ask whether a physical mechanism could plausibly connect the two — low pour temperature and incomplete fill, for instance. Statistics narrow the search; domain knowledge confirms the cause.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Question two",
      heading: "What do the bad parts have in common?",
      sub: "This is commonality analysis — the everyday detective work of recalls and warranty teams.",
    },
    {
      kind: "paragraph",
      text:
        "The second question is even more intuitive. Take every reject from the bad run and look at what they share that the good parts do not. Same heat? Same mould line? Same sand batch? Same shift? Commonality analysis is nothing more than that comparison done systematically instead of from memory. When ninety percent of the cracked castings trace to two heats that also happen to share a low magnesium reading, the common factor is no longer a hunch — it is sitting at the top of a list, sorted by how lopsided it is between the good population and the bad.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: share of rejects that trace to each candidate factor",
      unit: "% of rejects",
      max: 100,
      bars: [
        { label: "Heat 4471 + 4473 (low residual Mg)", value: 88, tone: "danger" },
        { label: "Sand batch run wet that morning", value: 71, tone: "warning" },
        { label: "Night shift", value: 34, tone: "info" },
        { label: "Pattern set B", value: 12, tone: "brand" },
      ],
      caption:
        "Generic, illustrative figures. The factor that dominates the reject population — and is rare in the good one — is your prime suspect.",
    },
    {
      kind: "pullQuote",
      quote:
        "You do not need to model the whole plant to find a cause. You need to know whether the defect tracks a parameter, and what the bad parts share that the good ones do not.",
      attribution: "The working analyst's shortcut",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Putting it together",
      heading: "From a spike to a fix, in four honest steps",
    },
    {
      kind: "processFlow",
      title: "A root-cause pass without the jargon",
      steps: [
        {
          title: "Isolate the bad",
          detail: "Define the reject population precisely — which defect, which window — using traceable records, not recollection.",
          icon: "search",
        },
        {
          title: "Scan correlations",
          detail: "Rank upstream parameters by how strongly they move with the defect across the whole history.",
          icon: "lineChart",
        },
        {
          title: "Test commonality",
          detail: "Find what the rejects share — heat, mould, sand, shift — that the good parts do not.",
          icon: "gitBranch",
        },
        {
          title: "Confirm the mechanism",
          detail: "Ask whether the top suspect is physically plausible, then change one thing and watch the number.",
          icon: "shieldCheck",
        },
      ],
      caption:
        "Each step is something a plant team already does informally. Writing them down is what turns a debate into a diagnosis.",
    },
    {
      kind: "paragraph",
      text:
        "The reason this usually fails today is not a lack of brains — it is a lack of joined-up data. If the defect log lives in one spreadsheet, the heat chemistry in another, and the sand tests in a third notebook by the muller, then correlation and commonality are technically possible but practically exhausting, so nobody does them. The bottleneck is plumbing, not statistics. Connect the records so that every reject already knows its heat, mould, sand batch and the conditions behind them, and these two questions become a few clicks rather than a few days.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Beware the confounded variable",
      text:
        "Two suspects often move together — a wet sand batch and the night shift that ran it. The way through is not heavier maths but more data: enough heats that the two eventually appear apart, letting you see which one actually carries the defect. Volume of history beats cleverness.",
    },
    {
      kind: "statCallout",
      value: "0",
      label: "data scientists required to ask the first useful question",
      caption:
        "Illustrative framing, not a dismissal of expertise. Specialists earn their keep on the hard residual cases — but the first, highest-value pass belongs to the people who run the process.",
      tone: "success",
    },
    {
      kind: "paragraph",
      text:
        "There will always be subtle problems that genuinely need a statistician, and when you hit one, the joined-up data you built makes their job possible too. But the everyday wins — the defect spike that turns out to be one wet sand batch, the porosity that tracks a single furnace — are not those problems. They yield to two plain questions, asked against data that has been connected once and for all. That is root-cause analysis demystified: less a discipline you hire for, more a habit you instrument for.",
    },
    {
      kind: "sahasEnables",
      intro:
        "Correlation and commonality are textbook techniques anyone can apply — the hard part is having the joined-up data to run them on. That is what the platform removes from the equation.",
      capabilities: [
        {
          title: "Correlation, surfaced for you",
          detail:
            "A built-in engine ranks how strongly each upstream parameter moves with a defect, with heatmaps and scatter views — no formulas to write.",
          icon: "lineChart",
        },
        {
          title: "Commonality on connected records",
          detail:
            "Because every reject already carries its heat, mould and sand lineage, 'what do the bad parts share?' is a filter, not a fortnight.",
          icon: "search",
        },
        {
          title: "An AI Casting Consultant for the why",
          detail:
            "An assistant explains a surfaced correlation in plain language and points to the heats behind it, so the insight lands with the people on the floor.",
          icon: "sparkles",
        },
      ],
    },
  ],
};
